import { useAuth } from '../auth/AuthContext'

const API_BASE = 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export type ExportFormat = 'markdown' | 'txt'

const formatExtension: Record<ExportFormat, string> = {
  markdown: 'md',
  txt: 'txt'
}

export function useExport() {
  const { firebaseUser } = useAuth()

  const exportDocument = async (documentId: string, format: ExportFormat = 'markdown') => {
    if (!firebaseUser) throw new Error('Not authenticated')

    const token = await firebaseUser.getIdToken()
    const tokenResult = await firebaseUser.getIdTokenResult()
    const orgId = tokenResult.claims.orgId
    const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/export?format=${format}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    })

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`)
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    const fileNameMatch = contentDisposition?.match(/filename="(.+)"/)
    const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : `document.${formatExtension[format]}`

    // Create blob and download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const bulkExport = async (documentIds: string[], format: ExportFormat = 'markdown') => {
    if (!firebaseUser) throw new Error('Not authenticated')

    if (documentIds.length === 0) {
      throw new Error('No documents selected')
    }

    if (documentIds.length > 10) {
      throw new Error('Cannot export more than 10 documents at once')
    }

    const token = await firebaseUser.getIdToken()
    const response = await fetch(`${API_BASE}/v1/export/bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        documentIds,
        format
      })
    })

    if (!response.ok) {
      throw new Error(`Bulk export failed: ${response.statusText}`)
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    const fileNameMatch = contentDisposition?.match(/filename="(.+)"/)
    const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : `bulk-export.${formatExtension[format]}`

    // Create blob and download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const exportAsMarkdown = (documentId: string) => exportDocument(documentId, 'markdown')
  const exportAsText = (documentId: string) => exportDocument(documentId, 'txt')

  const bulkExportAsMarkdown = (documentIds: string[]) => bulkExport(documentIds, 'markdown')
  const bulkExportAsText = (documentIds: string[]) => bulkExport(documentIds, 'txt')

  return {
    exportDocument,
    bulkExport,
    exportAsMarkdown,
    exportAsText,
    bulkExportAsMarkdown,
    bulkExportAsText
  }
}
